import React, { useMemo } from 'react'
import { Card, Fade, Tooltip } from '@mui/material/'
import { Avatar, IconButton, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { ReactComponent as SaveIcon } from '../../../../../../../assets/images/check-circle-1.svg'
import { ReactComponent as ActionIcon } from '../../../../../../../assets/images/actions/context.svg'

import SvgIcon from '@mui/material/SvgIcon'
import useIsHovered from '../../../../../../hooks/useIsHovered'
import { formatCommentCreationDate, getAuthorAvatarColor, getAuthorInitials } from './CommentListItem.helpers'
import { useCommentListItemStyles } from './CommentListItem.styles'
import { useSelector } from 'react-redux'
import { Comment } from '../../../../Comments.types'
import { useIsDarkMode } from '../../../../../../hooks/useIsDarkMode'
import clsx from 'clsx'
import { useIsAdmin } from '../../../../../../hooks/useIsAdmin'
import { ConditionalTooltipWrapper } from '../../../../../ConditionalTooltipWrapper'
import { useIsRoadmapReadonly } from '../../../../../../hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../../../../../hooks/useIsSnapshotMode'
import { CommentActionsMenu } from '../../../CommentActionsMenu/CommentActionsMenu'

type Props = {
    comment: Comment
    onRemove: () => Promise<void>
    onUpdateComment: (commentId: string, updates: { action?: string; isResolved?: boolean }) => Promise<void>
    isLoading: boolean
    isRemoving: boolean
    minimized: boolean
}

export const CommentListItem = ({ comment, onRemove, onUpdateComment, isLoading, isRemoving, minimized }: Props) => {
    const styles = useCommentListItemStyles()

    // @ts-expect-error store is not typed properly
    const userId = useSelector((state) => state.userRecord?._id)

    const isDarkMode = useIsDarkMode()
    const isReadonlyMode = useIsRoadmapReadonly()
    const isSnapshotMode = useIsSnapshotMode()

    const { isHovered, handlers } = useIsHovered()

    const isAdmin = useIsAdmin()

    const isYourComment = comment.user._id === userId
    const canEditCommentMeta = !isSnapshotMode && !isReadonlyMode
    const canRemove = isAdmin || (isYourComment && !comment.isResolved)

    const formattedText = useMemo(
        () => comment.text.split('\n').map((line, index) => <div key={index}>{line}</div>),
        [comment.text]
    )

    const avatarTooltipTitle = comment.isResolved ? 'Comment is resolved' : ''
    const isDateVisible = minimized || !isHovered

    return (
        <>
            <Box
                display="flex"
                justifyContent={isYourComment ? 'flex-end' : 'flex-start'}
                className={clsx(styles.container, minimized && styles.minimizedContainer)}
                {...handlers}
            >
                <Card
                    className={clsx(
                        styles.innerContainer,
                        minimized && styles.minimizedInnerContainer,
                        comment.isResolved && styles.resolved
                    )}
                    elevation={0}
                >
                    <Stack direction="column" gap={1}>
                        <Stack direction="row" gap={1.5} alignItems="center" position="relative">
                            <ConditionalTooltipWrapper
                                condition={!!avatarTooltipTitle}
                                title={avatarTooltipTitle}
                                arrow
                            >
                                <Box className={styles.avatarWrapper}>
                                    <Avatar
                                        alt={comment.user.name}
                                        className={styles.avatar}
                                        sx={{
                                            backgroundColor: getAuthorAvatarColor(comment.user.name, isDarkMode),
                                        }}
                                    >
                                        {getAuthorInitials(comment.user.name)}
                                    </Avatar>
                                    {comment.isResolved && (
                                        <Box className={styles.resolvedAvatarBadge}>
                                            <SvgIcon
                                                component={SaveIcon}
                                                inheritViewBox
                                                className={styles.resolvedAvatarIcon}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </ConditionalTooltipWrapper>

                            <Typography
                                className={clsx(styles.author, minimized && styles.minimizedAuthor)}
                                // @ts-expect-error theme is not typed properly
                                variant="title2"
                            >
                                {comment.user.name}
                            </Typography>
                            <Fade in={isDateVisible} timeout={150} unmountOnExit mountOnEnter>
                                <Typography
                                    className={styles.date}
                                    whiteSpace="nowrap"
                                    // @ts-expect-error theme is not typed properly
                                    variant="subtitle5"
                                >
                                    {formatCommentCreationDate(comment.createdAt)}
                                </Typography>
                            </Fade>
                        </Stack>
                        <Typography
                            className={clsx(styles.text, minimized && styles.minimizedText)}
                            // @ts-expect-error theme is not typed properly
                            variant="body4"
                        >
                            {formattedText}
                        </Typography>
                    </Stack>
                    <Box className={styles.menuAnchor}>
                        <CommentActionsMenu
                            comment={comment}
                            canRemove={canRemove}
                            canEditCommentMeta={canEditCommentMeta}
                            isLoading={isLoading}
                            isRemoving={isRemoving}
                            isVisible={isHovered}
                            onRemove={onRemove}
                            onToggleResolved={() => onUpdateComment(comment._id, { isResolved: !comment.isResolved })}
                            onSaveAction={(action) => onUpdateComment(comment._id, { action })}
                        />
                    </Box>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.25}
                        marginRight="-1px"
                        className={styles.actionsAnchor}
                    >
                        {comment.action && (
                            <Tooltip
                                placement="top-start"
                                arrow
                                title={
                                    <Typography
                                        variant="14x400"
                                        color="figma.grayscale.textHier1"
                                        sx={{
                                            whiteSpace: 'pre-line',
                                            overflow: 'auto',
                                            maxHeight: '50vh',
                                        }}
                                    >
                                        {comment.action}
                                    </Typography>
                                }
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            backgroundColor: 'figma.grayscale.contentWrapper',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '6px',
                                            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20)',
                                        },
                                    },
                                    arrow: {
                                        sx: {
                                            color: 'figma.grayscale.contentWrapper',
                                        },
                                    },
                                }}
                            >
                                <IconButton size="small" className={styles.actionIndicatorButton}>
                                    <SvgIcon
                                        component={ActionIcon}
                                        inheritViewBox
                                        fontSize="small"
                                        color="inherit"
                                        sx={{ fontSize: '20px' }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Card>
            </Box>
        </>
    )
}
